import { useMutation } from '@apollo/react-hooks';
import { useCallback } from 'react';
import { SocialAuthProvider } from '../../useSocialAuth';
import {
  CONNECT_USER_MUTATION,
  ConnectUserMutation,
  ConnectUserMutationVariables,
  DisconnectUserMutation,
  DisconnectUserMutationVariables,
  DISCONNECT_USER_MUTATION
} from './useConnectUserAccountMutation.rest';

export const useConnectUserAccountMutation = () => {
  const [connect, { loading: connectLoading, error: connectError }] = useMutation<
    ConnectUserMutation,
    ConnectUserMutationVariables
  >(CONNECT_USER_MUTATION);

  const [disconnect, { loading: disconnectLoading, error: disconnectError }] = useMutation<
    DisconnectUserMutation,
    DisconnectUserMutationVariables
  >(DISCONNECT_USER_MUTATION);

  const handleConnect = useCallback(
    async (provider: SocialAuthProvider, accessToken: string) => {
      const result = await connect({
        variables: {
          input: {
            data: {
              type: 'social_connection',
              attributes: {
                // eslint-disable-next-line @typescript-eslint/camelcase
                access_token: accessToken,
                provider: provider.toLowerCase()
              }
            }
          }
        }
      });

      return result.data?.connectUser.data.attributes;
    },
    [connect]
  );

  const handleDisconnect = useCallback(
    async (provider: SocialAuthProvider) => {
      const result = await disconnect({
        variables: {
          input: {
            data: {
              type: 'social_connection',
              attributes: {
                provider: provider.toLowerCase()
              }
            }
          }
        }
      });

      return result.data?.disconnectUser.data.attributes;
    },
    [disconnect]
  );

  const connectUser = useCallback(
    (provider: SocialAuthProvider, accessToken: string) => handleConnect(provider, accessToken),
    [handleConnect]
  );

  const disconnectUser = useCallback((provider: SocialAuthProvider) => handleDisconnect(provider), [
    handleDisconnect
  ]);

  return {
    connectUser,
    disconnectUser,
    loading: connectLoading || disconnectLoading,
    error: connectError || disconnectError
  } as const;
};
